import React from 'react';
import { Phone, AlertTriangle, CheckCircle, Gauge, Info } from 'lucide-react';
export function TiresWheels() {
  return <div className="min-h-screen bg-gray-50" data-id="element-1425">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white py-16" data-id="element-1426">
        <div className="container mx-auto px-4" data-id="element-1427">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-1428">
            Authorized Cooper Tire Dealer
          </h1>
          <p className="text-xl text-gray-100" data-id="element-1429">
            Expert Tire Service, Selection & Maintenance
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12" data-id="element-1430">
        <div className="max-w-4xl mx-auto" data-id="element-1431">
          {/* Tire Pressure Information */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1432">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-1433">
              <Gauge className="mr-3 text-[#B1050B]" size={32} data-id="element-1434" />
              Tire Pressure Maintenance
            </h2>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mb-6" data-id="element-1435">
              <p className="text-gray-800 mb-4" data-id="element-1436">
                It's possible for a passenger tire initially inflated to{' '}
                <strong data-id="element-1437">35 psi</strong> (pounds per square inch) to lose{' '}
                <strong data-id="element-1438">1 psi each month</strong>.
              </p>
              <p className="text-gray-800" data-id="element-1439">
                Be sure to properly check all four tires <strong data-id="element-1440">cold</strong>{' '}
                once a month and before a long trip. Whether you have a
                full-size or mini spare, make sure that it is properly inflated
                as well.
              </p>
            </div>

            <div className="space-y-4 text-gray-700" data-id="element-1441">
              <p data-id="element-1442">
                Beware of public pressure gauges at gas stations. They are often
                abused and unreliable.
              </p>
              <p data-id="element-1443">
                Purchase a good pressure gauge and check it for accuracy with
                your Michelin retailer.
              </p>
              <p className="font-semibold" data-id="element-1444">
                Check your tires <strong data-id="element-1445">cold</strong> – before you've driven
                even one mile or at least three hours after the vehicle has been
                stopped.
              </p>
            </div>
          </div>

          {/* Front End Alignment */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1446">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center" data-id="element-1447">
              Front End Alignment
            </h2>
            <p className="text-xl text-gray-700 text-center" data-id="element-1448">
              <strong data-id="element-1449">Front End Alignment should be done once a year</strong>
            </p>
          </div>

          {/* Tire Selection */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1450">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center" data-id="element-1451">
              Choose the tire you are looking for below
            </h2>
            <p className="text-gray-700 text-center mb-4" data-id="element-1452">
              Contact us to discuss your tire needs and get expert
              recommendations for your vehicle.
            </p>
            <div className="text-center" data-id="element-1453">
              <a href="tel:2484788382" className="inline-block bg-[#B1050B] hover:bg-[#8A0408] text-white px-6 py-3 rounded-lg font-semibold transition-colors" data-id="element-1454">
                Call for Tire Selection Help
              </a>
            </div>
          </div>

          {/* Tire Myths - Main Section */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1455">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center" data-id="element-1456">
              The Truth About Common Tire Myths
            </h2>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-8" data-id="element-1457">
              <p className="text-gray-700 mb-4" data-id="element-1458">
                Bigfoot, the Abominable Snowman and the Loch Ness Monster are
                all harmless myths. But there are stories on one subject that
                are passed on from generation to generation that should be put
                to rest, according to Dave Laubie, Bridgestone/Firestone
                director of sales engineering — tire myths.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-1459">
                "It's unfortunate that so much incorrect information about tires
                and tire care still persists," Laubie said. "Tire manufacturers,
                industry associations and tire dealers have worked hard to get
                the proper information out to consumers. Unfortunately, many
                people still rely on family 'experts' for advice."
              </p>
              <p className="text-gray-700" data-id="element-1460">
                "The result of these myths is dissatisfaction with the tires,
                the tire manufacturer and the dealer that sold the tires,"
                Laubie said. "But proper tire maintenance is still the
                responsibility of the driver." Improperly maintained tires can
                lead to problems and, at the very least, a shortened tire life.
              </p>
            </div>

            <p className="text-gray-700 mb-8" data-id="element-1461">
              In an effort to separate fact from fiction, Laubie and his team of
              engineers at Bridgestone/Firestone have prepared a list of common
              tire myths, as well as their corresponding tire facts.
            </p>

            <div className="space-y-8" data-id="element-1462">
              {/* Myth 1 - Air Pressure */}
              <div className="border-l-4 border-red-500 pl-6" data-id="element-1463">
                <h3 className="text-xl font-bold text-red-700 mb-2 flex items-center" data-id="element-1464">
                  <AlertTriangle className="mr-2" size={20} data-id="element-1465" />
                  MYTH:
                </h3>
                <p className="text-gray-700 mb-4" data-id="element-1466">
                  The best air pressure for a tire is on the tire's sidewall.
                </p>
                <h3 className="text-xl font-bold text-green-700 mb-2 flex items-center" data-id="element-1467">
                  <CheckCircle className="mr-2" size={20} data-id="element-1468" />
                  FACT:
                </h3>
                <p className="text-gray-700" data-id="element-1469">
                  The air pressure indicated on the sidewall of the tire is the
                  recommended maximum air pressure for that particular tire at a
                  specified load, not necessarily the air pressure for the tire
                  on your vehicle. Automobiles and tires are designed to work
                  together to provide the best balance of handling, traction,
                  comfort and braking characteristics. Unless you've changed
                  tire size and type, the proper air pressure for your vehicle
                  and tire combination may be found in your car's owner's
                  manual, on a sticker in the glovebox or affixed to the door
                  jamb.
                </p>
              </div>

              {/* Myth 2 - All-Season Tires */}
              <div className="border-l-4 border-red-500 pl-6" data-id="element-1470">
                <h3 className="text-xl font-bold text-red-700 mb-2 flex items-center" data-id="element-1471">
                  <AlertTriangle className="mr-2" size={20} data-id="element-1472" />
                  MYTH:
                </h3>
                <p className="text-gray-700 mb-4" data-id="element-1473">
                  All-season radial tires are good substitutes for snow tires.
                </p>
                <h3 className="text-xl font-bold text-green-700 mb-2 flex items-center" data-id="element-1474">
                  <CheckCircle className="mr-2" size={20} data-id="element-1475" />
                  FACT:
                </h3>
                <p className="text-gray-700" data-id="element-1476">
                  It depends. In general, all-season are the best tires for
                  year-round usage. However, if you live in an area that
                  receives little plowing or heavy snow, you should consider
                  switching to a snow tire during the winter.
                </p>
              </div>

              {/* Myth 3 - Spare Tires */}
              <div className="border-l-4 border-red-500 pl-6" data-id="element-1477">
                <h3 className="text-xl font-bold text-red-700 mb-2 flex items-center" data-id="element-1478">
                  <AlertTriangle className="mr-2" size={20} data-id="element-1479" />
                  MYTH:
                </h3>
                <p className="text-gray-700 mb-4" data-id="element-1480">
                  You can drive as fast and as long on a temporary or compact
                  spare tire as you can on a regular tire.
                </p>
                <h3 className="text-xl font-bold text-green-700 mb-2 flex items-center" data-id="element-1481">
                  <CheckCircle className="mr-2" size={20} data-id="element-1482" />
                  FACT:
                </h3>
                <p className="text-gray-700" data-id="element-1483">
                  Temporary or compact spare tires are designed to allow you to
                  get to the nearest tire dealer or service center for the
                  repair or replacement of your flat tire. Temporary spares
                  aren't designed for long-term use. The temporary spare should
                  be replaced by a regular size spare tire as soon as possible.
                  Follow your vehicle's owner's manual for proper usage of
                  temporary tires.
                </p>
              </div>

              {/* Myth 4 - Four-Wheel Drive */}
              <div className="border-l-4 border-red-500 pl-6" data-id="element-1484">
                <h3 className="text-xl font-bold text-red-700 mb-2 flex items-center" data-id="element-1485">
                  <AlertTriangle className="mr-2" size={20} data-id="element-1486" />
                  MYTH:
                </h3>
                <p className="text-gray-700 mb-4" data-id="element-1487">
                  I don't need snow tires on my four-wheel-drive vehicle.
                </p>
                <h3 className="text-xl font-bold text-green-700 mb-2 flex items-center" data-id="element-1488">
                  <CheckCircle className="mr-2" size={20} data-id="element-1489" />
                  FACT:
                </h3>
                <p className="text-gray-700" data-id="element-1490">
                  A four-wheel-drive vehicle with all-season tires usually is
                  better in the snow than a two-wheel-drive vehicle with
                  all-seasons tires. However most vehicles will perform better
                  in the snow with snow tires rather than with all-season tires.
                  Remember: four-wheel-drive vehicles are great to get you going
                  in the snow, but they are no better than two-wheel-drive
                  vehicles at stopping or turning on snowy or icy surfaces.
                </p>
              </div>

              {/* Myth 5 - UTQG Rating */}
              <div className="border-l-4 border-red-500 pl-6" data-id="element-1491">
                <h3 className="text-xl font-bold text-red-700 mb-2 flex items-center" data-id="element-1492">
                  <AlertTriangle className="mr-2" size={20} data-id="element-1493" />
                  MYTH:
                </h3>
                <p className="text-gray-700 mb-4" data-id="element-1494">
                  The Uniform Tire Quality Grade (UTQG) rating is directly
                  related to mileage for all tire manufacturers.
                </p>
                <h3 className="text-xl font-bold text-green-700 mb-2 flex items-center" data-id="element-1495">
                  <CheckCircle className="mr-2" size={20} data-id="element-1496" />
                  FACT:
                </h3>
                <p className="text-gray-700" data-id="element-1497">
                  The treadwear or mileage portion of UTQG is an indicator
                  within that brand of tires. It's not a good indicator between
                  brands of tires because of the broad interpretation of the
                  standard.
                </p>
              </div>

              {/* Myth 6 - Uneven Wear */}
              <div className="border-l-4 border-red-500 pl-6" data-id="element-1498">
                <h3 className="text-xl font-bold text-red-700 mb-2 flex items-center" data-id="element-1499">
                  <AlertTriangle className="mr-2" size={20} data-id="element-1500" />
                  MYTH:
                </h3>
                <p className="text-gray-700 mb-4" data-id="element-1501">
                  Uneven wear on a tire means that the tire is defective or that
                  there are broken components within the tire.
                </p>
                <h3 className="text-xl font-bold text-green-700 mb-2 flex items-center" data-id="element-1502">
                  <CheckCircle className="mr-2" size={20} data-id="element-1503" />
                  FACT:
                </h3>
                <p className="text-gray-700" data-id="element-1504">
                  Uneven wear can result from many causes, most commonly
                  improper inflation pressure for the load being carried, lack
                  of rotation at recommended intervals and mechanical
                  conditions, such as worn or damaged suspension parts and poor
                  wheel alignment. In rare instances, it can indicate a problem
                  within the tire structure. In all cases, tires exhibiting
                  uneven wear should be examined by an authorized tire service
                  person so that the cause can be corrected.
                </p>
              </div>

              {/* Myth 7 - Vibration */}
              <div className="border-l-4 border-red-500 pl-6" data-id="element-1505">
                <h3 className="text-xl font-bold text-red-700 mb-2 flex items-center" data-id="element-1506">
                  <AlertTriangle className="mr-2" size={20} data-id="element-1507" />
                  MYTH:
                </h3>
                <p className="text-gray-700 mb-4" data-id="element-1508">
                  A vehicle ride disturbance or vibration is caused by tires.
                </p>
                <h3 className="text-xl font-bold text-green-700 mb-2 flex items-center" data-id="element-1509">
                  <CheckCircle className="mr-2" size={20} data-id="element-1510" />
                  FACT:
                </h3>
                <p className="text-gray-700" data-id="element-1511">
                  A vibration felt when the vehicle is in motion is the result
                  of a rotating component being out of balance, loose or
                  excessively worn. The tires are only one of many components in
                  a vehicle. Other causes of vibration may include wheels, brake
                  rotors or drums, driveshafts, tie rods, ball joints and CV
                  joints. In the event the tire is wearing irregularly, a
                  vibration or increase in the tires' noise level may occur.
                </p>
              </div>

              {/* Myth 8 - Warranty */}
              <div className="border-l-4 border-red-500 pl-6" data-id="element-1512">
                <h3 className="text-xl font-bold text-red-700 mb-2 flex items-center" data-id="element-1513">
                  <AlertTriangle className="mr-2" size={20} data-id="element-1514" />
                  MYTH:
                </h3>
                <p className="text-gray-700 mb-4" data-id="element-1515">
                  My tire is covered by my vehicle manufacturer's warranty,
                  including punctures and cuts.
                </p>
                <h3 className="text-xl font-bold text-green-700 mb-2 flex items-center" data-id="element-1516">
                  <CheckCircle className="mr-2" size={20} data-id="element-1517" />
                  FACT:
                </h3>
                <p className="text-gray-700" data-id="element-1518">
                  Tire manufacturers' warranties cover workmanship and materials
                  on the tires. Your vehicle's coverage does not include tires.
                  The tire manufacturers' warranties do not cover use-related
                  damages such as cuts, nail holes or impacts. However, some
                  tire retailers do sell road hazard warranties at the time of a
                  new replacement tire purchase, which can cover these types of
                  problems. These warranties generally are not offered by the
                  manufacturer, but by the individual tire dealer.
                </p>
              </div>

              {/* Myth 9 - Tire Age */}
              <div className="border-l-4 border-red-500 pl-6" data-id="element-1519">
                <h3 className="text-xl font-bold text-red-700 mb-2 flex items-center" data-id="element-1520">
                  <AlertTriangle className="mr-2" size={20} data-id="element-1521" />
                  MYTH:
                </h3>
                <p className="text-gray-700 mb-4" data-id="element-1522">
                  As long as I have tread remaining on my tires, they are safe
                  to use regardless of how old they may be.
                </p>
                <h3 className="text-xl font-bold text-green-700 mb-2 flex items-center" data-id="element-1523">
                  <CheckCircle className="mr-2" size={20} data-id="element-1524" />
                  FACT:
                </h3>
                <p className="text-gray-700" data-id="element-1525">
                  Rubber — the main component in tires — deteriorates with age
                  and exposure to the elements, like heat sources, sunlight and
                  the ozone. It therefore virtually is impossible to put an age
                  limit on a tire. However, if the tire appears weather-cracked
                  and old, it probably is, and should be taken in for evaluation
                  by a professional.
                </p>
              </div>

              {/* Myth 10 - Blowouts */}
              <div className="border-l-4 border-red-500 pl-6" data-id="element-1526">
                <h3 className="text-xl font-bold text-red-700 mb-2 flex items-center" data-id="element-1527">
                  <AlertTriangle className="mr-2" size={20} data-id="element-1528" />
                  MYTH:
                </h3>
                <p className="text-gray-700 mb-4" data-id="element-1529">
                  My tire just blew out for no reason; it must be defective.
                </p>
                <h3 className="text-xl font-bold text-green-700 mb-2 flex items-center" data-id="element-1530">
                  <CheckCircle className="mr-2" size={20} data-id="element-1531" />
                  FACT:
                </h3>
                <p className="text-gray-700" data-id="element-1532">
                  In most circumstances, the source of air loss is something
                  cutting, puncturing or bursting the tread or sidewall of a
                  tire.
                </p>
              </div>

              {/* Myth 11 - Mixing Tires */}
              <div className="border-l-4 border-red-500 pl-6" data-id="element-1533">
                <h3 className="text-xl font-bold text-red-700 mb-2 flex items-center" data-id="element-1534">
                  <AlertTriangle className="mr-2" size={20} data-id="element-1535" />
                  MYTH:
                </h3>
                <p className="text-gray-700 mb-4" data-id="element-1536">
                  It's acceptable to mix different types or sizes of car tires.
                </p>
                <h3 className="text-xl font-bold text-green-700 mb-2 flex items-center" data-id="element-1537">
                  <CheckCircle className="mr-2" size={20} data-id="element-1538" />
                  FACT:
                </h3>
                <p className="text-gray-700" data-id="element-1539">
                  Some combinations are acceptable; some are not. To determine
                  acceptable combinations, follow your vehicle's owner's manual
                  or consult a qualified tire service person.
                </p>
              </div>
            </div>

            <p className="text-sm text-gray-600 italic mt-8" data-id="element-1540">
              Source: Bridgestone
            </p>
          </div>

          {/* Tire Selection Info */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1541">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1542">
              Select Tires for my Vehicle
            </h2>

            <p className="text-gray-700 mb-4" data-id="element-1543">
              Do you know what tire is right for your vehicle? If not, contact
              us for expert guidance.
            </p>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-6" data-id="element-1544">
              <p className="text-sm text-gray-700" data-id="element-1545">
                <strong data-id="element-1546">Important Note:</strong> The fitments provided by tire
                selectors have been calculated by Tire Guides On-Line. The
                information contained in the Tire Guide On-Line data software
                applies only to standard vehicles that have not been modified.
                In addition, it should be understood that while these
                applications guidelines are accurate to the best of our
                knowledge, they are only guidelines. Whenever an individual
                servicing a vehicle notices conditions that are not covered by
                the recommendations, his expertise and judgment must supersede
                these recommendations.
              </p>
            </div>
          </div>

          {/* Tire Markings Info */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1547">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center" data-id="element-1548">
              Understanding Tire Markings
            </h2>
            <p className="text-gray-700 text-center mb-6" data-id="element-1549">
              This picture explains all the markings on your tires.
            </p>
            <div className="flex justify-center mb-6" data-id="element-1550">
              <img src="https://drwzpk38qkpfb.cloudfront.net/www.garrettauto.com/uploaded/images/tires.jpg400x400.jpg?opt=w3" alt="Tire markings explained" className="max-w-full h-auto rounded-lg shadow-md" data-id="element-1551" />
            </div>
            <div className="bg-gray-50 rounded-lg p-6 text-center" data-id="element-1552">
              <Info className="mx-auto text-[#B1050B] mb-3" size={48} data-id="element-1553" />
              <p className="text-gray-700" data-id="element-1554">
                Ask us about tire markings and what they mean for your vehicle
                during your next visit.
              </p>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg shadow-xl p-8 text-center" data-id="element-1555">
            <h2 className="text-3xl font-bold mb-4" data-id="element-1556">
              Need Tire Service or Have Questions?
            </h2>
            <p className="text-xl mb-6" data-id="element-1557">
              <strong data-id="element-1558">Any questions? Give us a call</strong>
            </p>
            <a href="tel:2484788382" className="inline-flex items-center bg-[#B1050B] hover:bg-[#8A0408] text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-1559">
              <Phone className="mr-3" size={24} data-id="element-1560" />
              Call (248) 478-8382
            </a>
            <p className="mt-4 text-gray-300" data-id="element-1561">
              24175 Industrial Park Dr., Farmington Hills, MI 48335
            </p>
          </div>
        </div>
      </div>
    </div>;
}